import { cloneDeep, isEmpty } from 'lodash';

import { QuoteType } from '@common/types';
import { TERM_FAST_QUOTE_STEPS, TERM_QUOTE_STEPS } from '@termquote/constants';

import { useTermQuoteStore } from './quote';
import { Premium, TermAddOn, TermProductDetails } from './types';

/**
 * get the product boundary for a product
 *
 * @param product
 * @returns TermProductBoundary
 */
export const getProductDetails = (
  product: string
): TermProductDetails | undefined => {
  const productsList = useTermQuoteStore.getState().productsList;
  if (!isEmpty(productsList)) {
    const productDetails = productsList.find(
      (productDetail) => productDetail.product.key === product
    );
    return productDetails ?? undefined;
  }
  return undefined;
};

/**
 * Calculate the completion percentage based on the current step and total no of steps
 *
 * @param step
 * @param quote
 * @returns number
 */
export const getStepProgress = (step: number, quote: QuoteType) => {
  const index =
    quote === QuoteType.Normal
      ? Object.values(TERM_QUOTE_STEPS).findIndex((item) => item === step)
      : Object.values(TERM_FAST_QUOTE_STEPS).findIndex((item) => item === step);
  return quote === QuoteType.Normal
    ? ((index + 1) / Object.keys(TERM_QUOTE_STEPS).length) * 100
    : ((index + 1) / Object.keys(TERM_FAST_QUOTE_STEPS).length) * 100;
};

/**
 * Check if there is a rider premium overlap with current addOns in store.
 *
 * @param premium
 * @param currAddOns
 * @returns boolean
 */
export const checkRiderPremiumOverlap = (
  premium: Premium,
  currAddOns?: TermAddOn[]
) => {
  const plan = useTermQuoteStore.getState().plan;
  const boundaries = getProductDetails(plan.product)?.boundaries;
  const storeAddOns = useTermQuoteStore.getState().addOns;
  const addOns = currAddOns ? currAddOns : storeAddOns;
  if (boundaries && boundaries.preventRiderPremiumOverlap) {
    const totalAddOnsPremium = addOns.reduce((total: number, addOn: TermAddOn) => {
      return addOn.included &&
        !addOn.inclusiveInBasePremium &&
        addOn.additionalPremium &&
        addOn.additionalPremium.base > 0
        ? total + addOn.additionalPremium.base
        : total;
    }, 0);
    if (totalAddOnsPremium > premium.basePlan.base) {
      return true;
    }
  }

  return false;
};

/**
 * Check if including an addon can cause premium overlap
 * @param premium
 * @param addOnKey
 */
export const checkRiderPremiumOverlapWithAddOn = (
  premium: Premium,
  addOnKey: string
) => {
  const plan = useTermQuoteStore.getState().plan;
  const boundaries = getProductDetails(plan.product)?.boundaries;
  const storeAddOns = cloneDeep(useTermQuoteStore.getState().addOns);
  const addOns = storeAddOns.map((addon) => {
    if (addon.key === addOnKey) {
      addon.included = true;
    }
    return addon;
  });
  // If the base premium includes addon premiums, then we don't need to check for overlap
  if (boundaries && boundaries.preventRiderPremiumOverlap) {
    const totalAddOnsPremium = addOns.reduce((total: number, addOn: TermAddOn) => {
      return addOn.included &&
        !addOn.inclusiveInBasePremium &&
        addOn.additionalPremium &&
        addOn.additionalPremium.base > 0
        ? total + addOn.additionalPremium.base
        : total;
    }, 0);
    if (totalAddOnsPremium > premium.basePlan.base) {
      return true;
    }
  }

  return false;
};
